/**
 * User Admin V2 Constants
 *
 * This file contains constant values used throughout the User Admin V2 page.
 */

// User status values
export const USER_STATUS = {
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  DELETED: 'DELETED',
};

// User status display labels
export const USER_STATUS_LABELS = {
  [USER_STATUS.ACTIVE]: 'Active',
  [USER_STATUS.SUSPENDED]: 'Suspended',
  [USER_STATUS.DELETED]: 'Deleted',
};

// User status chip colors
export const USER_STATUS_COLORS = {
  [USER_STATUS.ACTIVE]: 'success',
  [USER_STATUS.SUSPENDED]: 'warning',
  [USER_STATUS.DELETED]: 'error',
};

// User status options for dropdown
export const USER_STATUS_OPTIONS = [
  { value: USER_STATUS.ACTIVE, label: USER_STATUS_LABELS[USER_STATUS.ACTIVE] },
  { value: USER_STATUS.SUSPENDED, label: USER_STATUS_LABELS[USER_STATUS.SUSPENDED] },
  { value: USER_STATUS.DELETED, label: USER_STATUS_LABELS[USER_STATUS.DELETED] },
];

// GroupRole values (from GraphQL schema enum)
export const GROUP_ROLES = {
  APP_ADMIN: 'APP_ADMIN',
  CLIENT_ADMIN: 'CLIENT_ADMIN',
  MANAGER: 'MANAGER',
  ASSOCIATE: 'ASSOCIATE',
};

// GroupRole display labels
export const GROUP_ROLE_LABELS = {
  [GROUP_ROLES.APP_ADMIN]: 'App Admin',
  [GROUP_ROLES.CLIENT_ADMIN]: 'Client Admin',
  [GROUP_ROLES.MANAGER]: 'Manager',
  [GROUP_ROLES.ASSOCIATE]: 'Associate',
};

// GroupRole options for dropdown
export const GROUP_ROLE_OPTIONS = [
  { value: GROUP_ROLES.APP_ADMIN, label: GROUP_ROLE_LABELS[GROUP_ROLES.APP_ADMIN] },
  { value: GROUP_ROLES.CLIENT_ADMIN, label: GROUP_ROLE_LABELS[GROUP_ROLES.CLIENT_ADMIN] },
  { value: GROUP_ROLES.MANAGER, label: GROUP_ROLE_LABELS[GROUP_ROLES.MANAGER] },
  { value: GROUP_ROLES.ASSOCIATE, label: GROUP_ROLE_LABELS[GROUP_ROLES.ASSOCIATE] },
];

// Group (Department) values
export const GROUPS = {
  OPERATIONS: 'OPERATIONS',
  FINANCE: 'FINANCE',
  HUMAN_RESOURCES: 'HUMAN_RESOURCES',
  INFORMATION_TECHNOLOGY: 'INFORMATION_TECHNOLOGY',
  MARKETING: 'MARKETING',
  EXECUTIVE: 'EXECUTIVE',
};

// Group display labels
export const GROUP_LABELS = {
  [GROUPS.OPERATIONS]: 'Operations',
  [GROUPS.FINANCE]: 'Finance',
  [GROUPS.HUMAN_RESOURCES]: 'Human Resources',
  [GROUPS.INFORMATION_TECHNOLOGY]: 'Information Technology',
  [GROUPS.MARKETING]: 'Marketing',
  [GROUPS.EXECUTIVE]: 'Executive',
};

// Group options for dropdown
export const GROUP_OPTIONS = [
  { value: GROUPS.OPERATIONS, label: GROUP_LABELS[GROUPS.OPERATIONS] },
  { value: GROUPS.FINANCE, label: GROUP_LABELS[GROUPS.FINANCE] },
  { value: GROUPS.HUMAN_RESOURCES, label: GROUP_LABELS[GROUPS.HUMAN_RESOURCES] },
  { value: GROUPS.INFORMATION_TECHNOLOGY, label: GROUP_LABELS[GROUPS.INFORMATION_TECHNOLOGY] },
  { value: GROUPS.MARKETING, label: GROUP_LABELS[GROUPS.MARKETING] },
  { value: GROUPS.EXECUTIVE, label: GROUP_LABELS[GROUPS.EXECUTIVE] },
];

// OrgLevel values
export const ORG_LEVELS = {
  ORGLEVEL_1: 'ORGLEVEL_1',
  ORGLEVEL_2: 'ORGLEVEL_2',
  ORGLEVEL_3: 'ORGLEVEL_3',
  LOCATION_LEVEL: 'LOCATION_LEVEL',
};

// OrgLevel display labels
export const ORG_LEVEL_LABELS = {
  [ORG_LEVELS.ORGLEVEL_1]: 'Organization Level 1',
  [ORG_LEVELS.ORGLEVEL_2]: 'Organization Level 2',
  [ORG_LEVELS.ORGLEVEL_3]: 'Organization Level 3',
  [ORG_LEVELS.LOCATION_LEVEL]: 'Location Level',
};

// OrgLevel options for dropdown
export const ORG_LEVEL_OPTIONS = [
  { value: ORG_LEVELS.ORGLEVEL_1, label: ORG_LEVEL_LABELS[ORG_LEVELS.ORGLEVEL_1] },
  { value: ORG_LEVELS.ORGLEVEL_2, label: ORG_LEVEL_LABELS[ORG_LEVELS.ORGLEVEL_2] },
  { value: ORG_LEVELS.ORGLEVEL_3, label: ORG_LEVEL_LABELS[ORG_LEVELS.ORGLEVEL_3] },
  { value: ORG_LEVELS.LOCATION_LEVEL, label: ORG_LEVEL_LABELS[ORG_LEVELS.LOCATION_LEVEL] },
];

// Table column IDs
export const COLUMN_IDS = {
  FULL_NAME: 'fullName',
  EMAIL: 'email',
  GROUP_ROLE: 'groupRole',
  ROLE: 'role',
  STATUS: 'status',
  ACTIONS: 'actions',
};

// Table pagination options
export const PAGINATION_OPTIONS = [25, 50, 100];

// Default pagination size
export const DEFAULT_PAGE_SIZE = 25;

// Client ID for role field visibility (only grm client shows role field)
export const ROLE_FIELD_CLIENT_ID = 'grm';

// Form field validation
export const VALIDATION = {
  EMAIL_MAX_LENGTH: 100,
  FIRST_NAME_MAX_LENGTH: 50,
  FIRST_NAME_MIN_LENGTH: 1,
  LAST_NAME_MAX_LENGTH: 50,
  LAST_NAME_MIN_LENGTH: 1,
  USERNAME_MAX_LENGTH: 100,
  USERNAME_MIN_LENGTH: 3,
  EMPLOYEE_ID_MAX_LENGTH: 50,
};

// Toast notification messages
export const MESSAGES = {
  USER_CREATED_SUCCESS: 'User created successfully',
  USER_UPDATED_SUCCESS: 'User updated successfully',
  USER_DEACTIVATED_SUCCESS: 'User deactivated successfully',
  USER_CREATE_ERROR: 'Failed to create user',
  USER_UPDATE_ERROR: 'Failed to update user',
  USER_DEACTIVATE_ERROR: 'Failed to deactivate user',
  EMAIL_ALREADY_EXISTS: 'A user with this email already exists',
  NETWORK_ERROR: 'Network error. Please try again.',
  PERMISSION_DENIED: 'You do not have permission to perform this action',
  CANNOT_EDIT_SELF: 'You cannot edit or deactivate your own account',
};

// Search debounce delay (ms)
export const SEARCH_DEBOUNCE_DELAY = 300;

// AWS Cognito configuration
export const COGNITO = {
  DEFAULT_TEMP_PASSWORD_LENGTH: 12,
};
